/**
 * 单标签页锁模块
 * 使用 Web Locks API 限制同一浏览器只能有一个活跃标签页
 */

import { showDialog } from 'vant'
import { useGlobalStore } from '@/stores/global'
import { t } from '@/locales'
import { getHashPath } from '@/utils/route'
import { useUserStore } from '@/stores/user'

/**
 * 不需要显示单标签页提示的路由白名单
 * 这些路由通常是不需要登录的公开页面
 */
const INACTIVE_TAB_DIALOG_WHITELIST = [
  '/privacy-policy', // 隐私政策
  '/terms-of-use', // 使用条款
  '/community-guidelines', // 社区准则
  '/payment/success', // 支付成功
  '/payment/failure', // 支付失败
  '/oauth/google-callback', // Google OAuth 回调
  '/oauth/apple-callback', // Apple OAuth 回调
  '/devtool-warning', // 开发者工具警告
]

/**
 * 检查当前路由是否在白名单中
 * @returns {boolean} 如果在白名单中返回 true，否则返回 false
 */
function isInWhitelist() {
  const currentPath = getHashPath(window.location.href)

  return INACTIVE_TAB_DIALOG_WHITELIST.some(whitelistPath => {
    // 精确匹配
    if (currentPath === whitelistPath) {
      return true
    }

    // 其他路径：前缀匹配（当前路径是白名单路径的子路径）
    return currentPath.startsWith(whitelistPath + '/')
  })
}

/**
 * 显示非活跃标签页提示弹窗
 * 如果当前路由在白名单中，则不显示提示
 */
function showInactiveTabDialog() {
  // 检查是否在白名单中
  if (isInWhitelist()) {
    return
  }

  showDialog({
    title: t('common.inactiveTab.title'),
    message: t('common.inactiveTab.message'),
    confirmButtonText: t('common.inactiveTab.confirm'),
    closeOnClickOverlay: false
  })
}

/**
 * 获取应用锁（单标签页限制）
 * 只有活跃标签页才会执行 initUserState 和登录操作
 */
export async function acquireAppLock() {
  const globalStore = useGlobalStore()
  const userStore = useUserStore()
  // 设置初始化状态
  globalStore.setUserStateInitializing(true)
  // 检查浏览器是否支持 Web Locks API
  if (!navigator.locks) {
    console.warn('[SingleTab] Web Locks API not supported, running without lock')
    globalStore.setActiveTab(true)
    await userStore.initUserState()
    globalStore.setUserStateInitializing(false)

    return
  }

  const lockName = 'app-single-tab-lock'

  try {
    // 请求锁,只有一个标签页能获得锁
    await navigator.locks.request(
      lockName,
      {
        mode: 'exclusive',
        ifAvailable: true,
      },
      async (lock) => {
        if (lock) {
          console.log('[SingleTab] Lock acquired, this tab is now the active tab')
          globalStore.setActiveTab(true)

          await userStore.initUserState()
          globalStore.setUserStateInitializing(false)

          // 保持锁直到标签页关闭
          // 返回一个永不 resolve 的 Promise,让锁一直持有
          return new Promise(() => {
            console.log('[SingleTab] Holding lock until tab closes')
          })
        } else {
          console.warn('[SingleTab] Failed to acquire lock, this tab is inactive')
          globalStore.setUserStateInitializing(false)
          globalStore.setActiveTab(false)
          showInactiveTabDialog()
        }
      })
  } catch (error) {
    console.error('[SingleTab] Error acquiring lock:', error)
    globalStore.setUserStateInitializing(false)
    globalStore.setActiveTab(false)
    showInactiveTabDialog()
  }
}
